import { FC } from 'react';

import { LmSelectNew } from '@mes-ui/lemma';
import { Box, Stack, useMediaQuery } from '@mui/material';
import { commonTheme } from 'portfolio3/styles/theme';
import { BaseInputLabel, FormControl, Textarea } from 'portfolio3/ui-kit';
import { YearPicker, YearPickerController } from 'portfolio3/ui-kit/datePickers';
import { Select } from 'portfolio3/ui-kit/selects';
import { IController } from 'portfolio3/ui-kit/types';
import { isDefined, mergeSx } from 'utils';

import { IDictionaryItem, ITeacherDictionaryItem } from '../../../../api/types';
import { IconInfoOutline } from '../../../../icons';
import { Tooltip } from '../../../../ui-kit';
import { IThanksFormData } from '../types';
import { isSubjectRequired } from '../utils';
import * as styles from './styles';

import './index.scss';

interface IThanksFormProps {
  studentClassLevel: number;
  formData: IThanksFormData;
  onChangeFormData: (formData: IThanksFormData) => void;
  incrementTeachersPage: () => void;
  teachers: ITeacherDictionaryItem[];
  subjects: IDictionaryItem[];
  isTeachersLoading: boolean;
}

const ThanksForm: FC<IThanksFormProps> = ({
  studentClassLevel,
  formData,
  onChangeFormData,
  incrementTeachersPage,
  teachers,
  subjects,
  isTeachersLoading,
}) => {
  const isMobile = useMediaQuery(commonTheme.breakpoints.down('commonSm'));
  const renderMode = 'fixed';
  const inputSize = 'medium';

  const handleTeacherChange = (newTeacher: { value: number; label: string } | null) => {
    onChangeFormData({
      ...formData,
      teacher: newTeacher,
    });
  };

  const subjectController: IController<string | undefined> = {
    handleChange(code) {
      const subjectCode = code ? Number(code) : undefined;
      const subject = subjects.find((subject) => subject.code === subjectCode);
      onChangeFormData({
        ...formData,
        subjectCode: subjectCode,
        subjectName: subject?.value,
      });
    },
    handleClear() {
      onChangeFormData({
        ...formData,
        subjectCode: undefined,
        subjectName: undefined,
      });
    },
  };

  const yearController: YearPickerController = {
    handleChange(value) {
      onChangeFormData({
        ...formData,
        yearEducation: value ?? undefined,
      });
    },
    handleClear() {
      onChangeFormData({
        ...formData,
        yearEducation: undefined,
      });
    },
  };

  const yearPickerTooltip = !isMobile && (
    <Tooltip
      arrow
      placement="top"
      width="230px"
      title="Укажи год обучения, в котором ты обучался(лась) у данного учителя"
    >
      <Box sx={styles.yearPickerTooltipButton}>
        <IconInfoOutline sx={styles.tooltipIcon} />
      </Box>
    </Tooltip>
  );

  const { yearEducation, gratitudeText, teacher } = formData;

  const currentDate = new Date();
  const maxYear = currentDate.getFullYear();
  const minYear = new Date(maxYear - studentClassLevel, 0).getFullYear();
  const isYearError = isDefined(yearEducation) && (yearEducation > maxYear || yearEducation < minYear);

  const isSubjectFieldRequired = isSubjectRequired(formData.entityFormData);
  const subjectDisabled = subjects.length === 1;

  const computedGratitudeText = gratitudeText && teacher?.label ? `${teacher?.label.trim()}!\n` + gratitudeText : '';

  return (
    <div className="thanks-form">
      <FormControl
        renderMode={renderMode}
        inputSize={inputSize}
        label={null}
        control={
          <Stack className="teacher-control-container" gap="8px">
            <LmSelectNew
              required
              hasBottomLoader={isTeachersLoading}
              options={teachers}
              value={formData.teacher}
              onChange={handleTeacherChange}
              placeholder="Выбери учителя..."
              label="ФИО учителя"
              multiple={false}
              grouped={false}
              handleInfinityScroll={incrementTeachersPage}
            />
          </Stack>
        }
      />
      <FormControl
        required={isSubjectFieldRequired}
        renderMode={renderMode}
        inputSize={inputSize}
        disabled={subjectDisabled}
        control={
          <Select
            options={subjects}
            value={formData.subjectCode}
            controller={subjectController}
            placeholder="Выбери предмет..."
            inputRenderMode={renderMode}
            inputSize={inputSize}
          />
        }
        label={<BaseInputLabel>Предмет</BaseInputLabel>}
      />
      <Box sx={styles.yearPickerContainer}>
        <FormControl
          sx={mergeSx(!isMobile && { maxWidth: '220px' })}
          error={isYearError}
          renderMode={renderMode}
          inputSize={inputSize}
          label={<BaseInputLabel>Год обучения</BaseInputLabel>}
          control={
            <YearPicker
              isMobile={isMobile}
              renderMode={renderMode}
              size={inputSize}
              value={formData.yearEducation ?? null}
              controller={yearController}
              minValue={minYear}
              maxValue={maxYear}
            />
          }
        />
        {yearPickerTooltip}
      </Box>
      <FormControl
        renderMode={renderMode}
        inputSize={inputSize}
        disabled
        label={<BaseInputLabel>Слова благодарности</BaseInputLabel>}
        control={
          <Textarea
            renderMode={renderMode}
            size={inputSize}
            value={computedGratitudeText}
            placeholder="Сформируются автоматически после выбора учителя"
            controller={{ handleChange: () => null }}
          />
        }
      />
    </div>
  );
};

export default ThanksForm;
